import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { GUID, Tenant } from '@ndvr/auth-api';
import { CommandCenterHousehold, GlobalAccount } from '@ndvr/customer-api';
import type { Tenant as CustomerTenant } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { Toast } from 'widgets';

const MoveHouseholdsQueryKeys = {
    tenants: ['moveHouseholds', 'tenants'] as const,
    households: (tenantGUID: string) => ['moveHouseholds', 'households', tenantGUID] as const,
};

export const useMoveHouseholdsTenantsQuery = () =>
    useQuery<CustomerTenant[]>({
        queryKey: MoveHouseholdsQueryKeys.tenants,
        queryFn: async () => {
            const [tenants] = await CustomerApi().getTenants();
            return tenants ?? [];
        },
    });

export const useMoveHouseholdsListQuery = (tenantGUID: GUID<Tenant> | null) =>
    useQuery<CommandCenterHousehold[]>({
        queryKey: MoveHouseholdsQueryKeys.households(tenantGUID ?? ''),
        enabled: tenantGUID !== null,
        queryFn: async () => {
            const [households] = await CustomerApi().getCommandCenterHouseholdList(tenantGUID!);
            return households ?? [];
        },
    });

export const useMoveHouseholdMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            tenantGUID,
            globalAccountGUID,
        }: {
            tenantGUID: GUID<Tenant>;
            globalAccountGUID: GUID<GlobalAccount>;
        }) => {
            await CustomerApi().moveGlobalAccountsToTenant(tenantGUID, [globalAccountGUID], { movePersons: true });
        },
        onSuccess: () => {
            Toast.success('Household moved successfully.');
            queryClient.invalidateQueries({ queryKey: ['moveHouseholds', 'households'] });
        },
        onError: (error: unknown) => {
            Toast.forError(error, 'Failed to move household.');
        },
    });
};
